import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import { FormField } from "@/components/ui/FormField";

export const rangeSchema = z.object({
  Range: z.string().min(2, "Range must be at least 2 characters"),
});

export type RangeFormData = z.infer<typeof rangeSchema>;

export interface AddRangeFormHandle {
  submit: () => void;
  setFieldError: (field: string, message: string) => void;
  clearFieldErrors: () => void; reset?: () => void;
}

interface AddRangeFormProps {
  onSubmit: (data: RangeFormData) => void;
  defaultValues?: RangeFormData;
  isLoading?: boolean;
}

const AddRangeForm = forwardRef<AddRangeFormHandle, AddRangeFormProps>(
  ({ onSubmit, defaultValues, isLoading }, ref) => {
    const form = useForm();
    const {
      control,
      handleSubmit,
      formState: { errors },
      clearErrors,
      setError,
      reset,
    } = useForm<RangeFormData>({
      resolver: zodResolver(rangeSchema),
      defaultValues: defaultValues || {
        Range: "",
      },
    });

    useEffect(() => {
      if (defaultValues) {
        reset(defaultValues);
      }
    }, [defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
      setFieldError: (field: string, message: string) => {
        setError(field as keyof RangeFormData, { type: "server", message });
      },
      clearFieldErrors: () => clearErrors(),
      reset: () => {
      form.reset();
  }
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={control}
          label="Range"
          name="Range"
          error={errors.Range?.message}
          required
          placeholder="e.g. 15km-20km"
          disabled={isLoading}
          onClear={() => clearErrors("Range")}
        />
      </form>
    );
  }
);

AddRangeForm.displayName = "AddRangeForm";

export default AddRangeForm;
